import { t } from 'i18next'
import { tinyMCEFontMenuModel } from '@/components/global/TinyMCE/tinyMCEFonts'

type FontEntry = { name: string; displayName: string; family: string }
type FontGroup = { id: string; label: string; fonts: FontEntry[] }

/**
 * Flat lookup by lowercased bare family name → menu entry. We need:
 *   - `entry.name` to preserve the original casing from the font config
 *     ("Lato", "Noto Sans Bengali") for the button label.
 *   - `entry.family` to apply the full chain (with the script fallback) as
 *     the inline style so the label previews the typeface.
 * `getActiveFamilyName` returns the lowercased first family from the
 * active selection's font-family CSS, so we index by that key.
 */
const ENTRY_BY_NAME: Map<string, FontEntry> = new Map(
  [
    ...tinyMCEFontMenuModel.regular,
    ...tinyMCEFontMenuModel.groups.flatMap((g) => g.fonts),
    ...(tinyMCEFontMenuModel.other ? tinyMCEFontMenuModel.other.fonts : []),
  ].map((entry) => [entry.name.toLowerCase(), entry]),
)

/**
 * Title-case a lowercased family name as a heuristic for legacy fonts that
 * aren't in our font config (e.g. `font-family: 'helvetica neue'` from an
 * old saved doc). Capitalizes the first letter of each whitespace-separated
 * word. Doesn't touch fonts whose registered name is intentionally all-
 * lowercase or all-uppercase — those go through the ENTRY_BY_NAME path
 * which preserves the configured casing verbatim.
 */
const titleCase = (s: string): string =>
  s.replace(/\b\w/g, (c) => c.toUpperCase())

type FontToggleItem = {
  type: 'togglemenuitem'
  text: string
  active: boolean
  onAction: () => void
  // `meta.style` is consumed by TinyMCE 5's Silver theme `renderStyledText`
  // — it wraps the menu item's label text in a div with the supplied
  // inline styles. This is the same mechanism the stock `fontselect`
  // button uses to render each font name in its own typeface (see
  // `getPreviewFor` in node_modules/tinymce/themes/silver/theme.js).
  //
  // Optional. Top-level Latin items always include it; regional submenu
  // items also include it so previews render in the typeface (otherwise
  // "Noto Sans Bengali — বাংলা" tofus to boxes for the Bengali sample).
  // The fetch cost is scoped to submenu-open: `getSubmenuItems` is a lazy
  // TinyMCE callback that only fires when the user hovers that region's
  // trigger, so opening the toolbar alone never touches CJK/Indic woff2s.
  // When a submenu does open, all faces share `font-display: swap` from
  // `google-fonts.css`, so the menu stays interactive and items swap to
  // their typeface as each woff2 arrives.
  meta?: {
    style: {
      tag: 'div'
      styles: { 'font-family': string }
    }
  }
}

type FontNestedItem = {
  type: 'nestedmenuitem'
  text: string
  getSubmenuItems: () => FontToggleItem[]
}

// Non-interactive label that heads the "Used in this layout" section (#2435).
// TinyMCE 5 has no header item type, so a disabled menuitem is the idiomatic
// section label — same shape the stock menus use for inert rows.
type FontHeaderItem = {
  type: 'menuitem'
  text: string
  disabled: true
  onAction: () => void
}

type FontSeparatorItem = { type: 'separator' }

type FontMenuItem =
  | FontToggleItem
  | FontNestedItem
  | FontHeaderItem
  | FontSeparatorItem

/**
 * Read the first family name from the active font, lowercased and stripped
 * of quotes/whitespace. Mirrors the upstream TinyMCE 5.10 `fontselect`
 * helper at `themes/silver/theme.js`'s `getFirstFont`.
 */
const getFirstFamilyName = (fontFamily: string): string => {
  if (!fontFamily) return ''
  const first = fontFamily.split(/\s*,\s*/)[0] || ''
  return first
    .replace(/^['"]+|['"]+$/g, '')
    .trim()
    .toLowerCase()
}

/**
 * Resolve the active font-family for the current selection using TinyMCE's
 * built-in `queryCommandValue('FontName')` — the same API the stock
 * `fontselect` button uses. Walks ancestors, handles split selections, and
 * normalizes browser quirks; safer than reading `editor.dom.getStyle`
 * computed values manually.
 */
const getActiveFamilyName = (editor: Editor): string => {
  const fontFamily =
    (editor.queryCommandValue?.('FontName') as string | undefined) || ''
  return getFirstFamilyName(fontFamily)
}

/**
 * onAction parity with stock `fontselect`: wrap the FontName command in an
 * undoManager transaction so it groups cleanly with surrounding edits, and
 * call `editor.focus()` first so focus returns to the editor after the
 * menu closes — without this, the next keystroke goes nowhere.
 */
const applyFontFamily = (editor: Editor, family: string) => {
  editor.undoManager.transact(() => {
    editor.focus()
    editor.execCommand('FontName', false, family)
  })
}

const buildToggleItem = (
  editor: Editor,
  entry: FontEntry,
  activeName: string,
  includePreviewStyle: boolean,
): FontToggleItem => ({
  type: 'togglemenuitem',
  text: entry.displayName,
  // Case-insensitive match — browsers normalize font-family casing
  // inconsistently, and the upstream fontselect compares lowercased.
  active: entry.name.toLowerCase() === activeName,
  onAction: () => applyFontFamily(editor, entry.family),
  ...(includePreviewStyle
    ? {
        meta: {
          style: {
            tag: 'div' as const,
            styles: { 'font-family': entry.family },
          },
        },
      }
    : {}),
})

const buildGroupItem = (
  editor: Editor,
  group: FontGroup,
  activeName: string,
): FontNestedItem => ({
  type: 'nestedmenuitem',
  text: group.label,
  // `getSubmenuItems` is a lazy TinyMCE callback — only invoked when the
  // user actually opens this regional submenu. Building items here with
  // `includePreviewStyle: true` defers the woff2 fetch burst to that
  // moment instead of eating it on every toolbar open, while keeping the
  // own-typeface preview accurate (otherwise non-Latin sample text tofus).
  getSubmenuItems: () =>
    group.fonts.map((entry: FontEntry) =>
      buildToggleItem(editor, entry, activeName, true),
    ),
})

/**
 * Update the rendered toolbar button's visible label to mirror the current
 * selection's font. TinyMCE 5's public `ToolbarMenuButtonInstanceApi` does
 * not expose `setText`, so we mutate the rendered `.tox-tbtn__select-label`
 * directly. Scoped to this editor's container so multiple editor instances
 * don't fight over each other's labels.
 *
 * Best-effort: if the skin DOM ever changes shape, the dropdown still
 * works — only the label-mirroring stops.
 */
const updateButtonLabel = (editor: Editor): void => {
  const container = editor.getContainer?.()
  if (!container) return
  // Starts-with on aria-label because we also MUTATE aria-label below to
  // include the active font name (so the hover tooltip shows "Fonts - Lato"
  // instead of just "Fonts"). The CSS in tinyMCEStyles.ts and the E2E
  // selector both use `[aria-label^="Fonts"]` for the same reason.
  const button = container.querySelector<HTMLElement>('[aria-label^="Fonts"]')
  if (!button) return
  const label = button.querySelector<HTMLElement>('.tox-tbtn__select-label')
  if (!label) return
  const current = getActiveFamilyName(editor)
  if (!current) {
    label.textContent = 'Fonts'
    label.style.fontFamily = ''
    button.setAttribute('aria-label', 'Fonts')
    button.setAttribute('title', 'Fonts')
    return
  }
  // For fonts we ship: use the entry's `name` verbatim so the label
  // preserves the font config's casing (e.g. "Lato", "Noto Sans Bengali")
  // — including intentional all-caps or all-lowercase config. For legacy
  // fonts not in our config, title-case the lowercased name as a fallback
  // so old saved docs don't display the toolbar label as "helvetica neue".
  // The font-family inline style picks up the full chain (with the script
  // fallback) for shipped fonts; legacy fonts get a bare quoted family.
  const entry = ENTRY_BY_NAME.get(current)
  const displayName = entry ? entry.name : titleCase(current)
  label.textContent = displayName
  label.style.fontFamily = entry ? entry.family : `'${current}'`
  // TinyMCE 5.10's silver theme `getTooltipAttributes` sets BOTH `aria-label`
  // and `title` on the button at render time from the same `tooltip` spec
  // value. The native browser hover tooltip is the `title` attribute, NOT
  // aria-label — mutating aria-label alone changes the a11y label but leaves
  // the hover tooltip stale. Update both so the hover reads
  // "Fonts - Lato" / etc.
  const tooltipText = `Fonts - ${displayName}`
  button.setAttribute('aria-label', tooltipText)
  button.setAttribute('title', tooltipText)
}

/**
 * Register `customFontSelect` — a font-family menu button that replaces
 * TinyMCE 5's stock `fontselect`. Same toolbar slot and same per-item font
 * preview, but regional Noto script groups (Middle Eastern, South Asian,
 * etc.) are hover-out submenus instead of inline separator rows + their
 * member fonts dumped flat into the top level. See #2517 follow-up.
 *
 * The button's `tooltip` is kept as "Fonts" so existing aria-label
 * selectors (E2E + a11y tooling) continue to resolve.
 */
export const registerCustomFontSelectPlugin = ({
  editor,
}: {
  editor: Editor
}) => {
  editor.ui.registry.addMenuButton('customFontSelect', {
    text: 'Fonts',
    tooltip: 'Fonts',
    fetch: (callback: (items: FontMenuItem[]) => void) => {
      const activeName = getActiveFamilyName(editor)
      const items: FontMenuItem[] = []

      // "Used in this layout" section (#2435): the canonical font names live
      // on editor.customData.usedFonts, kept current by useLayoutUsedFonts.
      // Resolve each through ENTRY_BY_NAME so the rows reuse the same preview
      // styling and active-state matching as the rest of the menu. Read here
      // (not at register time) because fetch is lazy — it runs on every open,
      // so the section always reflects the latest applied fonts.
      const usedFontNames = editor.customData?.usedFonts ?? []
      const usedEntries = usedFontNames
        .map((name) => ENTRY_BY_NAME.get(name.toLowerCase()))
        .filter((entry): entry is FontEntry => Boolean(entry))

      if (usedEntries.length > 0) {
        items.push({
          type: 'menuitem',
          text: t('editor.tinyMCE.usedFonts.header'),
          disabled: true,
          onAction: () => {},
        })
        usedEntries.forEach((entry) => {
          items.push(buildToggleItem(editor, entry, activeName, true))
        })
        items.push({ type: 'separator' })
      }

      tinyMCEFontMenuModel.regular.forEach((entry: FontEntry) => {
        items.push(buildToggleItem(editor, entry, activeName, true))
      })

      tinyMCEFontMenuModel.groups.forEach((group: FontGroup) => {
        items.push(buildGroupItem(editor, group, activeName))
      })

      if (tinyMCEFontMenuModel.other) {
        items.push(
          buildGroupItem(editor, tinyMCEFontMenuModel.other, activeName),
        )
      }

      callback(items)
    },
    // onSetup fires once the toolbar button is rendered, so the initial label
    // paint reflects the document's current font without racing the toolbar
    // DOM (an `init` handler can fire before the button exists in inline
    // mode). The returned teardown runs when the button is destroyed —
    // editor.remove() or the i18n-key remount — so the NodeChange handler is
    // unsubscribed instead of leaking or firing into a half-removed editor.
    // Mirrors registerNameReorderPlugin's teardown contract.
    onSetup: () => {
      const syncLabel = () => updateButtonLabel(editor)
      syncLabel()
      editor.on('NodeChange', syncLabel)
      return () => editor.off('NodeChange', syncLabel)
    },
  })
}

export default registerCustomFontSelectPlugin
